// src/components/Atelier3/Activite3/PreliminaryLikelihoodModal.js
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Zap, AlertTriangle, Radar } from 'lucide-react';
import useAtelier2Store from '../../../stores/useAtelier2Store';
import workshop3Service from '../../../services/workshop3Service';
import { vraisemblanceFromCouple, getProposedLikelihood } from '../../../utils/vraisemblanceFromCouple';

const LEVEL_STYLES = {
  1: { selected: 'bg-green-600 text-white border-green-700 ring-green-300', idle: 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100' },
  2: { selected: 'bg-yellow-500 text-white border-yellow-600 ring-yellow-300', idle: 'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100' },
  3: { selected: 'bg-orange-500 text-white border-orange-600 ring-orange-300', idle: 'bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100' },
  4: { selected: 'bg-red-600 text-white border-red-700 ring-red-300', idle: 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100' },
};

const PreliminaryLikelihoodModal = ({ path, analysisId, onClose, onSave, onClear }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [couple, setCouple] = useState(null);
  const [proposed, setProposed] = useState(2);
  const [selected, setSelected] = useState(2);
  const [justification, setJustification] = useState('');
  const [hasOpScenarios, setHasOpScenarios] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const existing = path.preliminary?.active ? path.preliminary : null;
      try {
        const [couples, scenariosResponse] = await Promise.allSettled([
          useAtelier2Store.getState().getCouplesSROV(analysisId),
          workshop3Service.getOperationalScenarios(analysisId),
        ]);
        if (cancelled) return;

        const couplesList = couples.status === 'fulfilled' ? couples.value : [];
        const foundCouple = couplesList.find(
          (c) => String(c.sourceId) === String(path.sourceRiskId),
        ) || null;
        const opScenarios = scenariosResponse.status === 'fulfilled'
          ? (scenariosResponse.value?.data ?? scenariosResponse.value ?? [])
          : [];
        const matchedScenarios = Array.isArray(opScenarios)
          ? opScenarios.filter((s) => String(s.attackPathId) === String(path.id))
          : [];

        setCouple(foundCouple);
        setHasOpScenarios(matchedScenarios.length > 0);
        const proposal = getProposedLikelihood(foundCouple, path.niveau);
        setProposed(proposal);
        setSelected(existing?.likelihood || proposal);
        setJustification(existing?.justification || '');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [path.id, analysisId, path.sourceRiskId, path.niveau]);

  const coupleLabel = couple?.vraisemblance
    ? t(`workshop3.activity3.preliminary.coupleLabels.${couple.vraisemblance}`, couple.vraisemblance)
    : '';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-indigo-700">
          <h3 className="text-base font-bold text-white flex items-center">
            <Zap size={18} className="mr-2" />
            {t('workshop3.activity3.preliminary.title')}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">{t('workshop3.activity3.preliminary.description')}</p>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {hasOpScenarios && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">{t('workshop3.activity3.preliminary.warningTitle')}</p>
                    <p className="text-xs text-amber-700 mt-0.5">{t('workshop3.activity3.preliminary.warningMessage')}</p>
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('workshop3.activity3.preliminary.pathReferenceLabel')} — {path.referenceCode || path.id}</p>
                <p className="text-sm text-slate-700"><span className="font-medium">Source Risque:</span> {path.sourceRiskName}</p>
                <p className="text-sm text-slate-700"><span className="font-medium">Objectif Visé:</span> {path.objectifVise}</p>
              </div>

              <div className={couple ? "grid grid-cols-2 gap-3" : "grid grid-cols-1 gap-3"}>
                {couple && (
                  <div className="p-3 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Valeur SR/OV</p>
                    <p className="mt-1 text-sm font-medium text-slate-800">
                      {t('workshop3.activity3.preliminary.fromCouple', { value: vraisemblanceFromCouple(couple), label: coupleLabel || couple.vraisemblance })}
                    </p>
                  </div>
                )}
                <div className="p-3 rounded-lg border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('workshop3.activity3.preliminary.fromNiveau', { value: path.niveau || '—' })}</p>
                  <p className="mt-1 text-sm font-medium text-slate-800">
                    {couple
                      ? `N${path.niveau || '?'}`
                      : <span className="text-slate-700 italic">{t('workshop3.activity3.preliminary.noCouple', { value: path.niveau || '—' })}</span>}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {couple
                    ? t('workshop3.activity3.preliminary.proposedLabel')
                    : t('workshop3.activity3.preliminary.proposedLabelNiveau', { value: path.niveau || '—' })}{' '}
                  <span className={`ml-0.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${LEVEL_STYLES[proposed]?.selected.split(' ')[0] || 'bg-slate-500'}`}>{proposed}/4</span>
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map((level) => {
                    const style = LEVEL_STYLES[level];
                    const isSelected = selected === level;
                    return (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setSelected(level)}
                        className={`rounded-lg border px-2 py-2 text-center transition-colors ${
                          isSelected ? style.selected : style.idle
                        } ${isSelected ? 'ring-2 ring-offset-1' : ''}`}
                      >
                        <span className="block text-lg font-bold leading-none">{level}</span>
                        <span className="block text-[10px] mt-0.5 opacity-90">
                          {t(`workshop3.activity3.preliminary.levelLabels.${level}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  {t('workshop3.activity3.preliminary.justification')}
                </label>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder={t('workshop3.activity3.preliminary.justificationPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <p className="text-xs text-slate-400 flex items-center gap-1">
                <Radar size={12} />
                {t('workshop3.activity3.preliminary.usedInWorkshop5')}
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 bg-slate-50">
          <div>
            {path.preliminary?.active && (
              <button
                onClick={onClear}
                className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              >
                {t('workshop3.activity3.preliminary.deactivate')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              {t('workshop3.activity3.preliminary.cancel')}
            </button>
            <button
              onClick={() => onSave({ likelihood: selected, justification })}
              disabled={loading}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {path.preliminary?.active ? t('workshop3.activity3.preliminary.update') : t('workshop3.activity3.preliminary.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PreliminaryLikelihoodModal;